import type { AccessAnalysisReport, AccessAnalysisStatus, CoverageAnalysisReport, Satellite, SatGroup } from '../types/mission';

const getReportId = (raw: any): string => (
  raw?.reportId ??
  raw?.report_id ??
  raw?.id ??
  raw?.accessAnalysisReportId ??
  raw?.accessAnalysisId ??
  raw?.analysisId ??
  raw?.analysis_id ??
  raw?.runId ??
  raw?.run_id ??
  ''
);

const getReportName = (raw: any, fallbackIndex?: number): string => (
  raw?.name ??
  raw?.reportName ??
  raw?.report_name ??
  raw?.analysisName ??
  raw?.analysis_name ??
  (fallbackIndex != null ? `REPORT_${String(fallbackIndex + 1).padStart(2, '0')}` : 'REPORT')
);

const getReportStatus = (raw: any): AccessAnalysisStatus => (
  raw?.status ??
  raw?.reportStatus ??
  raw?.report_status ??
  raw?.analysisStatus ??
  raw?.analysis_status ??
  raw?.jobStatus ??
  raw?.job_status ??
  'completed'
);

export function mapAccessAnalysisReport(raw: any, fallbackIndex?: number): AccessAnalysisReport | null {
  const reportId = String(getReportId(raw));
  if (!reportId) return null;
  return {
    reportId,
    name: String(getReportName(raw, fallbackIndex)),
    status: String(getReportStatus(raw)).toLowerCase(),
    createdAt: raw?.createdAt ?? raw?.created_at ?? raw?.generatedAt ?? raw?.generated_at ?? raw?.timestamp ?? null,
    updatedAt: raw?.updatedAt ?? raw?.updated_at ?? null,
    raw,
  };
}

export function mapAccessAnalysisReportsResponse(payload: any): AccessAnalysisReport[] {
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload?.reports)
        ? payload.reports
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
  return list
    .map((item: any, index: number) => mapAccessAnalysisReport(item, index))
    .filter((item: AccessAnalysisReport | null): item is AccessAnalysisReport => !!item);
}

export function buildPendingAccessAnalysisReport(raw: any, fallbackIndex = 0): AccessAnalysisReport | null {
  const mapped = mapAccessAnalysisReport(raw, fallbackIndex);
  if (!mapped) return null;
  return {
    ...mapped,
    status: mapped.status === 'completed' ? 'analyzing' : mapped.status,
  };
}

export interface ReportUsedEntities {
  satelliteIds: Set<string>;
  groupIds: Set<string>;
  groundStationIds: Set<string>;
  geoFeatureIds: Set<string>;
}

export function getReportUsedEntities(
  report: AccessAnalysisReport | CoverageAnalysisReport | null,
  satellites: Array<Satellite | SatGroup>
): ReportUsedEntities {
  const satelliteIds = new Set<string>();
  const groupIds = new Set<string>();
  const groundStationIds = new Set<string>();
  const geoFeatureIds = new Set<string>();

  if (!report) {
    return { satelliteIds, groupIds, groundStationIds, geoFeatureIds };
  }

  const raw = report.raw ?? {};
  const input = raw.requestInput ?? raw.request_input ?? raw.input ?? raw.request ?? raw.requestBody ?? raw.request_body ?? raw;

  // Helpers to safely parse arrays from various key styles
  const getListValues = (...lists: any[]) => {
    const list = lists.find(item => Array.isArray(item));
    if (!list) return [];
    return list.map((item: any) => String(typeof item === 'string' ? item : item?.id ?? item?.objectId ?? item?.aoiId ?? '')).filter(Boolean);
  };

  // 1. Parse ground stations
  const gsIds = getListValues(input?.groundstationIds, input?.groundStationIds, input?.ground_station_ids);
  gsIds.forEach(id => groundStationIds.add(id));

  // 2. Parse geofeatures / AOIs / Targets
  const aoiIds = getListValues(input?.aoiIds, input?.aoi_ids, input?.geoFeatureIds, input?.geo_feature_ids, input?.aoiobjects);
  aoiIds.forEach(id => geoFeatureIds.add(id));

  // 3. Parse sources (satellites/groups)
  const groups = input?.groups ?? input?.groupIds ?? input?.group_ids ?? [];
  const sourceEntries = Array.isArray(groups)
    ? groups.map((item: any) => {
        if (typeof item === 'string') return { id: item, type: 'Single' };
        return {
          id: String(item?.objectId ?? item?.object_id ?? item?.id ?? ''),
          type: String(item?.objectType ?? item?.object_type ?? 'Single'),
        };
      }).filter((item) => item.id)
    : [];

  sourceEntries.forEach((source) => {
    const isGroupType = source.type.toLowerCase().includes('group');

    // Search for this source ID in the tree
    let foundGroup: SatGroup | null = null;
    let foundSat: Satellite | null = null;

    for (const item of satellites) {
      if ((item as SatGroup).type === 'group') {
        const group = item as SatGroup;
        if (group.id === source.id) {
          foundGroup = group;
          break;
        }
        const child = group.children?.find(sat => sat.id === source.id || sat.simulationGroupId === source.id);
        if (child) {
          foundSat = child;
          break;
        }
      } else {
        const sat = item as Satellite;
        if (sat.id === source.id || sat.simulationGroupId === source.id) {
          foundSat = sat;
          break;
        }
      }
    }

    if (isGroupType && foundGroup) {
      groupIds.add(foundGroup.id);
      foundGroup.children?.forEach(sat => {
        if (sat.id) {
          satelliteIds.add(sat.id);
        }
      });
    } else if (foundSat) {
      satelliteIds.add(foundSat.id);
    } else {
      // Fallback if not found in current list
      if (isGroupType) {
        groupIds.add(source.id);
      } else {
        satelliteIds.add(source.id);
      }
    }
  });

  return { satelliteIds, groupIds, groundStationIds, geoFeatureIds };
}
